import { Phone, MessageCircle, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Placeholder from "@/components/Placeholder";
import { Reveal, StaggerGroup, StaggerItem, GlowCard, AnimatedLine, FloatingBlob, Magnetic } from "@/components/Motion";
import { SITE, whatsappLink } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Contact() {
  return (
    <div data-testid="contact-page" className="relative overflow-hidden">
      <SEO title="Contact Us" description="Get in touch with WellMed Hospitals and Dr. Priyanka Gupta." />
      <FloatingBlob color="aurora-purple" size={500} top="-10%" left="-10%" delay={0} />

      <section className="py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="overline mb-4">Get in touch</div>
            <h1 className="font-heading text-5xl md:text-6xl text-[var(--color-text)] leading-[1.05] max-w-4xl">
              Ready to begin your <em className="text-[var(--color-coral)] not-italic">healthcare journey?</em>
            </h1>
            <p className="mt-6 text-lg text-[var(--color-text-muted)] max-w-2xl leading-relaxed">
              Reach us through any of the channels below. We respond personally — never with auto-replies.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 relative">
        <FloatingBlob color="aurora-coral" size={350} top="20%" right="-5%" delay={2} />
        <StaggerGroup className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-3 gap-5 relative z-10" stagger={0.1}>
          <StaggerItem>
            <a
              href={`tel:${SITE.phone}`}
              data-testid="contact-call-card"
              className="group glass-card block p-8 border border-[var(--color-border)] hover:border-[var(--color-coral)] transition-all h-full"
            >
              <div className="w-12 h-12 rounded-full bg-[var(--color-coral-light)] group-hover:bg-[var(--color-coral)] flex items-center justify-center mb-5 transition-all shadow-[0_0_15px_var(--color-coral-glow)] group-hover:shadow-[0_0_24px_var(--color-coral-glow)] border border-[var(--color-coral)]/30">
                <Phone className="w-5 h-5 text-[var(--color-coral)] group-hover:text-white transition" />
              </div>
              <div className="overline mb-2">Call Us</div>
              <h3 className="font-heading text-2xl text-white">{SITE.phoneDisplay}</h3>
              <p className="text-sm text-white/80 mt-2">Mon–Sat · 10:00 AM – 8:30 PM</p>
              <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-[var(--color-coral)] group-hover:gap-2 transition-all">
                Tap to call <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </a>
          </StaggerItem>

          <StaggerItem>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-card"
              className="group glass-card block p-8 border border-[var(--color-border)] hover:border-[#25D366] transition-all h-full"
            >
              <div className="w-12 h-12 rounded-full bg-[#25D366]/10 group-hover:bg-[#25D366] flex items-center justify-center mb-5 transition border border-[#25D366]/30 group-hover:shadow-[0_0_24px_rgba(37,211,102,0.3)]">
                <MessageCircle className="w-5 h-5 text-[#25D366] group-hover:text-white transition" />
              </div>
              <div className="overline mb-2" style={{ color: "#25D366" }}>WhatsApp</div>
              <h3 className="font-heading text-2xl text-white">Chat with us</h3>
              <p className="text-sm text-white/80 mt-2">Quick replies, voice notes welcome</p>
              <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-[#25D366] group-hover:gap-2 transition-all">
                Open chat <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </a>
          </StaggerItem>

          <StaggerItem>
            <a
              href={`tel:+919218008794`}
              data-testid="contact-facility-card"
              className="group glass-card block p-8 border border-[var(--color-border)] hover:border-[var(--color-coral)] transition-all h-full"
            >
              <div className="w-12 h-12 rounded-full bg-[var(--color-coral-light)] group-hover:bg-[var(--color-coral)] flex items-center justify-center mb-5 transition border border-[var(--color-coral)]/30 group-hover:shadow-[0_0_24px_var(--color-coral-glow)]">
                <Phone className="w-5 h-5 text-[var(--color-coral)] group-hover:text-white transition" />
              </div>
              <div className="overline mb-2">Facility Director</div>
              <h3 className="font-heading text-xl text-white">+91 92180 08794</h3>
              <p className="text-sm text-white/80 mt-2">Wellmed Hospitals</p>
              <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-[var(--color-coral)] group-hover:gap-2 transition-all">
                Tap to call <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </a>
          </StaggerItem>
        </StaggerGroup>
      </section>

      <section className="pb-24 relative">
        <FloatingBlob color="aurora-warm" size={400} bottom="10%" left="5%" delay={1} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-stretch relative">
          <Reveal className="lg:col-span-5 h-full">
            <div className="glass-card p-8 border border-[var(--color-border)] flex flex-col h-full">
              <div className="overline mb-3">Visit</div>
              <h2 className="font-heading text-3xl text-white leading-tight">Wellmed Hospitals</h2>
              <div className="mt-5 space-y-4 text-sm text-white/80 flex-1">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 text-[var(--color-coral)] shrink-0 flex-none" />
                  <span>{SITE.address}</span>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 mt-0.5 text-[var(--color-coral)] shrink-0 flex-none" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--color-coral)] font-semibold mb-1">Hospital</div>
                    <div>Open 24×7 — Emergency &amp; inpatient</div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--color-coral)] font-semibold mt-3 mb-1">Dr. Priyanka&apos;s consultation</div>
                    <div>Morning: 11:00 AM – 2:00 PM</div>
                    <div>Evening: 6:00 PM – 8:00 PM</div>
                    <div>Sunday: Closed</div>
                  </div>
                </div>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Magnetic>
                  <a
                    href={SITE.googleMaps}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary text-sm"
                    data-testid="contact-directions"
                  >
                    Get Directions <ArrowRight className="w-4 h-4" />
                  </a>
                </Magnetic>
                <Link to="/appointment" className="btn-outline text-sm" data-testid="contact-book-link">Book Online</Link>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7 h-full" delay={0.2}>
            <div className="rounded-3xl overflow-hidden border border-[var(--color-border)] min-h-[400px] h-full" data-testid="contact-map">
              <iframe
                title="WellMed Hospital Location"
                src={SITE.googleMapsEmbed}
                width="100%"
                height="100%"
                className="w-full h-full min-h-[400px] border-0 filter invert-[90%] hue-rotate-[180deg]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
